export const VALID_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;

export const MONTH_MAP: Record<string, string> = {
  JAN: '01',
  FEB: '02',
  MAR: '03',
  APR: '04',
  MAY: '05',
  JUN: '06',
  JUL: '07',
  AUG: '08',
  SEP: '09',
  OCT: '10',
  NOV: '11',
  DEC: '12',
};

export interface UpcomingDateParts {
  day: string;
  month: string;
  year: string;
}

export interface UpcomingDateErrors {
  day: string;
  month: string;
  year: string;
}

export const emptyUpcomingDateErrors = (): UpcomingDateErrors => ({ day: '', month: '', year: '' });

export const parseUpcomingDateInput = (date: string): UpcomingDateParts => {
  const [year, month, day] = date.split('-');
  const monthName = month ? Object.entries(MONTH_MAP).find(([, value]) => value === month)?.[0] || '' : '';
  return {
    day: day || '',
    month: monthName,
    year: year || '',
  };
};

export const validateUpcomingDayInput = (value: string): { value: string; error: string } => {
  if (value === '') {
    return { value: '', error: '' };
  }

  if (!/^\d+$/.test(value)) {
    return { value, error: 'Day must be a number (1-31)' };
  }

  if (value.length > 2) {
    return { value, error: 'Day must be 1-2 digits' };
  }

  const dayNum = parseInt(value, 10);
  if (value.length === 1 && dayNum === 0) {
    return { value, error: 'Day must be between 1-31' };
  }

  if (value.length === 2 && (dayNum < 1 || dayNum > 31)) {
    return { value, error: 'Day must be between 1-31' };
  }

  return { value, error: '' };
};

export const validateUpcomingMonthInput = (value: string): { value: string; error: string } => {
  const normalizedValue = value.toUpperCase();

  if (normalizedValue === '') {
    return { value: '', error: '' };
  }

  if (!/^[A-Z]+$/.test(normalizedValue)) {
    return { value: normalizedValue, error: 'Month must be letters (JAN, FEB, etc.)' };
  }

  if (normalizedValue.length > 3) {
    return { value: normalizedValue, error: 'Month must be 3 letters' };
  }

  if (normalizedValue.length > 0) {
    const possibleMonths = VALID_MONTHS.filter(month => month.startsWith(normalizedValue));
    if (possibleMonths.length === 0) {
      return { value: normalizedValue, error: 'Invalid month abbreviation' };
    }
  }

  if (normalizedValue.length === 3 && !VALID_MONTHS.includes(normalizedValue as typeof VALID_MONTHS[number])) {
    return { value: normalizedValue, error: 'Invalid month (use JAN, FEB, MAR, etc.)' };
  }

  return { value: normalizedValue, error: '' };
};

export const validateUpcomingYearInput = (
  value: string,
  currentYear: number,
  options?: { allowPastYears?: boolean }
): { value: string; error: string } => {
  if (value === '') {
    return { value: '', error: '' };
  }

  if (!/^\d+$/.test(value)) {
    return { value, error: 'Year must be a number' };
  }

  if (value.length > 4) {
    return { value, error: 'Year must be 4 digits' };
  }

  const yearNum = parseInt(value, 10);
  if (value.length === 4) {
    if (!options?.allowPastYears && yearNum < currentYear) {
      return { value, error: `Year must be ${currentYear} or later` };
    }
    return { value, error: '' };
  }

  return { value, error: '' };
};

export const validateUpcomingDateParts = (
  parts: UpcomingDateParts,
  currentYear: number,
  options?: { allowPastYears?: boolean }
): { parts: UpcomingDateParts; errors: UpcomingDateErrors } => {
  const day = validateUpcomingDayInput(parts.day);
  const month = validateUpcomingMonthInput(parts.month);
  const year = validateUpcomingYearInput(parts.year, currentYear, options);

  return {
    parts: {
      day: day.value,
      month: month.value,
      year: year.value,
    },
    errors: {
      day: day.error,
      month: month.error,
      year: year.error,
    },
  };
};

export const buildUpcomingDateValue = (
  parts: UpcomingDateParts,
  options?: { allowPastYears?: boolean }
): { date: string | null; error: string | null } => {
  const normalizedMonth = parts.month.toUpperCase();
  const monthValue = MONTH_MAP[normalizedMonth];

  if (!parts.day || !normalizedMonth || !parts.year || !monthValue) {
    return { date: null, error: 'Please fill in all date fields' };
  }

  const paddedDay = parts.day.padStart(2, '0');
  const date = `${parts.year}-${monthValue}-${paddedDay}`;
  const selectedDate = new Date(date + 'T00:00:00');
  const roundTrippedDate = selectedDate.toLocaleDateString('en-CA');
  if (roundTrippedDate !== date) {
    return { date: null, error: 'Invalid date: that day does not exist in the selected month' };
  }

  const today = new Date(new Date().toLocaleDateString('en-CA') + 'T00:00:00');
  if (!options?.allowPastYears && selectedDate < today) {
    return { date: null, error: 'Cannot create a date in the past' };
  }

  return { date, error: null };
};
